/**
 * Remembers where a signed-out visitor was headed so login can send them back
 * there (EMR-1159). Without this, a deep link — e.g. the "Go To Patient" button
 * on a RogerEMR card in the mobile app — drops the user on their default landing
 * page and the destination is lost.
 *
 * sessionStorage, not localStorage: the intent belongs to this tab's sign-in
 * attempt and should not outlive it.
 */

const STORAGE_KEY = 'roger.postLoginRedirect';

/**
 * How long a remembered destination stays valid. It exists to survive one
 * sign-in round trip — a redirect out to WorkOS and back, which takes seconds.
 * The auth routes sit outside ProtectedRoute, so without an expiry a clinician
 * who opens a deep link, abandons the login, then signs in again later in the
 * same tab would be dropped on the abandoned patient instead of their own
 * landing page.
 */
const MAX_AGE_MS = 5 * 60 * 1000;

/** Auth pages are never a destination — landing back on one would loop. */
const AUTH_PATHS = ['/login', '/auth/callback', '/signed-out'];

interface StoredRedirect {
  path: string;
  savedAt: number;
}

function isStorableTarget(path: string): boolean {
  // Must be a same-origin absolute path. `//host` is protocol-relative, and
  // browsers normalise a leading `/\` to `//` — both leave the site.
  if (!/^\/(?![/\\])/.test(path)) {
    return false;
  }
  // Compare on segment boundaries: `/login` is an auth page, `/logins` is not.
  return !AUTH_PATHS.some(
    p =>
      path === p || (path.startsWith(p) && /^[/?#]/.test(path.slice(p.length)))
  );
}

/** Reads and validates the stored entry. Anything malformed reads as absent. */
function readStored(): StoredRedirect | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const { path, savedAt } = parsed as Partial<StoredRedirect>;
  if (typeof path !== 'string' || typeof savedAt !== 'number') {
    return null;
  }
  // Re-validate on read: the entry could have been written by anything.
  if (!isStorableTarget(path)) {
    return null;
  }
  if (!Number.isFinite(savedAt) || Date.now() - savedAt > MAX_AGE_MS) {
    return null;
  }
  return { path, savedAt };
}

/** Call before redirecting an unauthenticated visitor to /login. */
export function rememberPostLoginRedirect(path: string): void {
  if (!isStorableTarget(path)) {
    return;
  }
  try {
    const entry: StoredRedirect = { path, savedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Private mode / storage disabled — fall back to the default landing page.
  }
}

/**
 * Drop any remembered path. Auth resolves in two steps — the store can read as
 * signed-out for a tick before `/auth/me` answers — so an already-signed-in user
 * can leave a path behind on the way in. Clearing it once they are through stops
 * that stale target from hijacking a later sign-in in the same tab.
 */
export function clearPostLoginRedirect(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was stored either.
  }
}

/**
 * Returns the remembered path and clears it. One-shot by design: the caller
 * must not invoke this twice (see the guard in AuthCallback).
 */
export function consumePostLoginRedirect(): string | null {
  const stored = readStored();
  clearPostLoginRedirect();
  return stored ? stored.path : null;
}

/** The current location, in the form the two functions above expect. */
export function currentPathForRedirect(): string {
  return `${window.location.pathname}${window.location.search}`;
}
